import { UserRole } from '../../types/auth.types'
import { api } from './apiClient'
import { API_CONFIG, API_ENDPOINTS } from '@config/api.config'
import type {
  LoginCredentials,
  LoginResponse,
  User,
  WhatsAppAuthRequest,
  WhatsAppAuthResponse,
  WhatsAppAuthStatus,
  WhatsAppPINVerification,
  StoredApiCredentials,
} from '../../types/auth.types'
import { mockAuthService } from './mockAuthService'
import { credentialStorageService } from './credentialStorageService'

// Check if we're in development/demo mode
const isDevelopment = import.meta.env.DEV
const isDemo = import.meta.env.VITE_ENV === 'demo'
const useMockAuth = import.meta.env.VITE_USE_MOCK_AUTH !== 'false' && isDevelopment && !isDemo

// Log authentication mode
if (useMockAuth) {
  console.info('🔧 Development Mode: Using Mock Authentication')
  console.info('📧 Demo Credentials: demo@aml-guard.com / demo123')
  console.info('✨ Or use any valid email format with any password')
} else if (isDemo) {
  console.info('🎯 Demo Mode: Using AML-Guard Demo Environment')
  console.info('🌐 API: https://demo.aml-guard.com/services')
  console.info('👤 User:', API_CONFIG.user.id)
  console.info('🔑 Authentication: Token-based (REST API)')
}

// Authentication Service
export const authService = {
  /**
   * Login with email and password
   */
  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    // Use mock authentication in development mode
    if (useMockAuth) {
      const mockResponse = await mockAuthService.login(credentials)

      // Store tokens
      localStorage.setItem('access_token', mockResponse.access_token)
      localStorage.setItem('refresh_token', mockResponse.refresh_token)
      localStorage.setItem('mock_user_email', credentials.email)

      return mockResponse
    }

    // Demo Mode: Use AML-Guard token authentication
    if (isDemo) {
      // For demo, we use the pre-configured token
      const token = API_CONFIG.auth.token

      if (!token) {
        throw new Error('Demo token not configured. Please check your .env file.')
      }

      // Store the token
      localStorage.setItem('access_token', token)
      localStorage.setItem('aml_guard_user', API_CONFIG.user.id)

      // Return mock response with user info
      return {
        access_token: token,
        refresh_token: token, // Demo: use same token
        user: {
          id: API_CONFIG.user.id,
          email: credentials.email || `${API_CONFIG.user.id}@aml-guard.com`,
          firstName: API_CONFIG.user.firstName,
          lastName: API_CONFIG.user.lastName,
          role: UserRole.COMPLIANCE_OFFICER,
          permissions: ['search', 'review', 'decide', 'manage_data_pools'],
          createdAt: new Date().toISOString(),
          lastLogin: new Date().toISOString(),
        }
      }
    }

    // Production: Use real API
    const response = await api.post<LoginResponse>(API_ENDPOINTS.auth.login, credentials)

    // Store tokens
    localStorage.setItem('access_token', response.data.access_token)
    localStorage.setItem('refresh_token', response.data.refresh_token)

    return response.data
  },

  /**
   * Logout current user
   */
  logout: async (): Promise<void> => {
    try {
      // Use mock service in development
      if (useMockAuth) {
        await mockAuthService.logout()
      } else {
        await api.post(API_ENDPOINTS.auth.logout)
      }
    } finally {
      // Clear tokens regardless of API response
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('mock_user_email')
    }
  },

  /**
   * Get current user profile
   */
  getProfile: async (): Promise<User> => {
    // Use mock service in development
    if (useMockAuth) {
      return await mockAuthService.getProfile()
    }

    // Demo Mode: Return user info from config
    if (isDemo) {
      const token = localStorage.getItem('access_token')
      const userId = localStorage.getItem('aml_guard_user')

      if (!token || !userId) {
        throw new Error('Not authenticated. Please login first.')
      }

      return {
        id: API_CONFIG.user.id,
        email: `${API_CONFIG.user.id}@aml-guard.com`,
        firstName: API_CONFIG.user.firstName,
        lastName: API_CONFIG.user.lastName,
        role: UserRole.COMPLIANCE_OFFICER,
        permissions: ['search', 'review', 'decide', 'manage_data_pools'],
        createdAt: new Date().toISOString(),
        lastLogin: new Date().toISOString(),
      }
    }

    // Production: Use real API
    const response = await api.get<User>(API_ENDPOINTS.auth.profile)
    return response.data
  },

  /**
   * Refresh access token
   */
  refreshToken: async (): Promise<LoginResponse> => {
    const refreshToken = localStorage.getItem('refresh_token')

    if (!refreshToken) {
      throw new Error('No refresh token available')
    }

    // Use mock service in development
    if (useMockAuth) {
      const mockResponse = await mockAuthService.refreshToken()

      // Store new tokens
      localStorage.setItem('access_token', mockResponse.access_token)
      if (mockResponse.refresh_token) {
        localStorage.setItem('refresh_token', mockResponse.refresh_token)
      }

      return mockResponse
    }

    // Production: Use real API
    const response = await api.post<LoginResponse>(API_ENDPOINTS.auth.refresh, {
      refresh_token: refreshToken,
    })

    // Store new tokens
    localStorage.setItem('access_token', response.data.access_token)
    if (response.data.refresh_token) {
      localStorage.setItem('refresh_token', response.data.refresh_token)
    }

    return response.data
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated: (): boolean => {
    const token = localStorage.getItem('access_token')
    return !!token
  },

  /**
   * Get stored access token
   */
  getAccessToken: (): string | null => {
    return localStorage.getItem('access_token')
  },

  // ============================================
  // WhatsApp Cross-Device Authentication Methods
  // ============================================

  /**
   * Request PIN - Initiates WhatsApp authentication
   * Sends magic link to user's WhatsApp and returns authRequestId for polling
   */
  requestPIN: async (request: WhatsAppAuthRequest): Promise<WhatsAppAuthResponse> => {
    // Use mock service in development mode
    if (useMockAuth) {
      return await mockAuthService.requestPIN(request)
    }

    // Production: Call WhatsApp API via Nginx proxy (avoids CORS)
    const response = await api.post<WhatsAppAuthResponse>(
      '/api/whatsapp/api/auth/request-pin',
      request
    )
    return response.data
  },

  /**
   * Check Authentication Status - Polling endpoint
   * Desktop browser polls this to check if user clicked magic link
   */
  checkAuthStatus: async (authRequestId: string): Promise<WhatsAppAuthStatus> => {
    // Use mock service in development mode
    if (useMockAuth) {
      return await mockAuthService.checkAuthStatus(authRequestId)
    }

    // Production: Call WhatsApp API via Nginx proxy
    const response = await api.get<WhatsAppAuthStatus>(
      `/api/whatsapp/api/auth/status/${authRequestId}`
    )

    // If authenticated, store tokens
    if (response.data.status === 'authenticated' && response.data.access_token) {
      localStorage.setItem('access_token', response.data.access_token)
      if (response.data.refresh_token) {
        localStorage.setItem('refresh_token', response.data.refresh_token)
      }
    }

    return response.data
  },

  /**
   * Verify PIN - Fallback manual PIN entry
   * Used when user cannot click magic link
   */
  verifyPIN: async (verification: WhatsAppPINVerification): Promise<LoginResponse> => {
    // Use mock service in development mode
    if (useMockAuth) {
      return await mockAuthService.verifyPIN(verification)
    }

    // Production: Call WhatsApp API via Nginx proxy
    const response = await api.post<LoginResponse>(
      '/api/whatsapp/api/auth/verify-pin',
      verification
    )

    // Store tokens
    localStorage.setItem('access_token', response.data.access_token)
    localStorage.setItem('refresh_token', response.data.refresh_token)

    return response.data
  },

  /**
   * Cancel pending WhatsApp authentication
   */
  cancelWhatsAppAuth: (): void => {
    localStorage.removeItem('whatsapp_auth_request_id')
    localStorage.removeItem('whatsapp_auth_phone')
  },

  // ============================================
  // Local Credential Storage Methods
  // Alternative authentication when remote API unavailable
  // ============================================

  /**
   * Check if stored credentials exist for a phone number
   */
  hasStoredCredentials: (phone: string): boolean => {
    return credentialStorageService.hasCredentials(phone)
  },

  /**
   * Get stored credentials for a phone number
   */
  getStoredCredentials: (phone: string): StoredApiCredentials | null => {
    return credentialStorageService.getCredentialsByPhone(phone)
  },

  /**
   * Get all stored credentials
   */
  getAllStoredCredentials: (): StoredApiCredentials[] => {
    return credentialStorageService.getAllCredentials()
  },

  /**
   * Authenticate using stored credentials for a phone number
   * This is used when the remote API is unavailable
   */
  loginWithStoredCredentials: async (phone: string): Promise<LoginResponse> => {
    const credentials = credentialStorageService.getCredentialsByPhone(phone)

    if (!credentials) {
      throw new Error('No stored credentials found for this phone number')
    }

    // Store the token
    localStorage.setItem('access_token', credentials.apiToken)
    localStorage.setItem('aml_guard_user', credentials.userId)

    // Update last used timestamp
    credentialStorageService.updateLastUsed(phone)

    // Create user object from stored credentials
    const user: User = {
      id: credentials.userId,
      email: `${credentials.userId}@aml-guard.com`,
      firstName: credentials.userName?.split(' ')[0] || credentials.userId,
      lastName: credentials.userName?.split(' ').slice(1).join(' ') || '',
      role: UserRole.COMPLIANCE_OFFICER,
      permissions: ['search', 'review', 'decide', 'manage_data_pools'],
      createdAt: credentials.createdAt,
      lastLogin: new Date().toISOString(),
    }

    return {
      access_token: credentials.apiToken,
      refresh_token: credentials.apiToken, // Use same token as refresh
      user,
    }
  },

  /**
   * Save API credentials for a phone number
   */
  saveCredentialsForPhone: (
    phone: string,
    apiToken: string,
    userId: string,
    userName?: string,
    company?: string
  ): { success: boolean; message?: string } => {
    const result = credentialStorageService.saveCredentials({
      phone,
      apiToken,
      userId,
      userName,
      company,
    })
    return { success: result.success, message: result.message }
  },

  /**
   * Delete stored credentials for a phone number
   */
  deleteStoredCredentials: (phone: string): boolean => {
    return credentialStorageService.deleteCredentials(phone)
  },

  /**
   * Request PIN with fallback to stored credentials
   * Checks for configured credentials first, then tries remote API
   */
  requestPINWithFallback: async (request: WhatsAppAuthRequest): Promise<WhatsAppAuthResponse & { useStoredCredentials?: boolean; useDemoCredentials?: boolean }> => {
    // Use mock service in development mode
    if (useMockAuth) {
      return await mockAuthService.requestPIN(request)
    }

    // Check if we have stored credentials for this phone
    const hasStored = credentialStorageService.hasCredentials(request.phone)

    // Check if we have configured credentials (from .env file)
    const hasConfiguredCredentials = !!(API_CONFIG.auth.token && API_CONFIG.user.id)

    console.info('📱 Auth check - hasToken:', !!API_CONFIG.auth.token, 'hasUserId:', !!API_CONFIG.user.id, 'hasConfigured:', hasConfiguredCredentials, 'hasStored:', hasStored)

    // If we have stored credentials for this phone, use them directly
    if (hasStored) {
      console.info('📱 Using stored credentials for:', request.phone)
      return {
        success: true,
        authRequestId: 'local-auth-' + Date.now(),
        message: 'Using stored credentials.',
        useStoredCredentials: true,
      }
    }

    // If we have configured credentials (from .env), use them directly
    // Skip remote API since it's likely not available
    if (hasConfiguredCredentials) {
      console.info('📱 Using configured credentials from environment')
      return {
        success: true,
        authRequestId: 'config-auth-' + Date.now(),
        message: 'Using configured credentials.',
        useStoredCredentials: true,
        useDemoCredentials: true,
      }
    }

    // No local credentials - try remote API
    try {
      console.info('📱 Trying remote WhatsApp API...')
      const response = await api.post<WhatsAppAuthResponse>(
        '/api/whatsapp/api/auth/request-pin',
        request
      )
      return response.data
    } catch (error: any) {
      console.info('📱 Remote WhatsApp API failed:', error.message)

      // Offer to configure credentials
      return {
        success: true,
        authRequestId: 'setup-auth-' + Date.now(),
        message: 'Remote API unavailable. Please configure credentials.',
        useStoredCredentials: true,
        useDemoCredentials: false,
      }
    }
  },

  /**
   * Login with configured credentials from .env
   * Used when remote API unavailable
   */
  loginWithDemoCredentials: async (phone: string): Promise<LoginResponse> => {
    if (!API_CONFIG.auth.token || !API_CONFIG.user.id) {
      throw new Error('No configured credentials available')
    }

    const token = API_CONFIG.auth.token

    // Store the token
    localStorage.setItem('access_token', token)
    localStorage.setItem('aml_guard_user', API_CONFIG.user.id)

    // Also save these credentials for future use with this phone
    credentialStorageService.saveCredentials({
      phone,
      apiToken: token,
      userId: API_CONFIG.user.id,
      userName: `${API_CONFIG.user.firstName} ${API_CONFIG.user.lastName}`.trim() || undefined,
      company: API_CONFIG.user.company || undefined,
    })

    // Create user object from config
    const user: User = {
      id: API_CONFIG.user.id,
      email: `${API_CONFIG.user.id}@aml-guard.com`,
      firstName: API_CONFIG.user.firstName || API_CONFIG.user.id,
      lastName: API_CONFIG.user.lastName || '',
      role: UserRole.COMPLIANCE_OFFICER,
      permissions: ['search', 'review', 'decide', 'manage_data_pools'],
      createdAt: new Date().toISOString(),
      lastLogin: new Date().toISOString(),
    }

    return {
      access_token: token,
      refresh_token: token,
      user,
    }
  },

  /**
   * Check if configured credentials are available
   */
  hasConfiguredCredentials: (): boolean => {
    return !!API_CONFIG.auth.token && !!API_CONFIG.user.id
  },

  /**
   * Get configured credentials info (for display)
   */
  getConfiguredCredentialsInfo: (): { token: string; userId: string; userName: string; company: string } | null => {
    if (!API_CONFIG.auth.token || !API_CONFIG.user.id) {
      return null
    }
    return {
      token: API_CONFIG.auth.token,
      userId: API_CONFIG.user.id,
      userName: `${API_CONFIG.user.firstName} ${API_CONFIG.user.lastName}`.trim(),
      company: API_CONFIG.user.company || '',
    }
  },
}

export default authService
